import React, { useCallback, useEffect, useState } from 'react';

import { FieldPath, useForm, useWatch } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';

import Button from 'components/Button/Button';
import Input from 'components/Input/Input';
import Switch from 'components/Switch/Switch';
import Typography from 'components/Typography/Typography';
import { Form, FormField } from 'components/ui';
import { Sheet, SheetContent } from 'components/ui/sheet';

import { appsFlyerS2SDefaults } from '../../DeveloperSettings.constants';

import { appsFlyerS2SSchema } from './appsFlyerS2S.schema';
import { ApplicationIdRow } from './elements/ApplicationIdRow';
import { DisableConfirmDialog } from './elements/DisableConfirmDialog';
import { EventRow } from './elements/EventRow';
import {
  AppsFlyerS2SDrawerProps,
  AppsFlyerS2SFormValues,
  ServerFieldError
} from './types';

const SECTION_DIVIDER =
  "relative after:content-[''] after:absolute after:bottom-0 after:left-9 after:right-9 after:h-px after:bg-base-sidebarBorder";

type ServerGroupErrors = { appIds?: string; events?: string };

const normalizeServerField = (field: string): string =>
  field.replace(/^appIds\.(\d+)\.value$/, 'appIds.$1');

const DEFAULT_VALUES: AppsFlyerS2SFormValues = {
  enabled: false,
  s2sToken: '',
  appIds: [''],
  events: {
    'payment-success': {
      enabled: true,
      eventName: appsFlyerS2SDefaults.purchaseEventName
    },
    refund: {
      enabled: true,
      eventName: appsFlyerS2SDefaults.refundEventName
    }
  }
};

const buildDefaultValues = (
  initialValues?: AppsFlyerS2SFormValues
): AppsFlyerS2SFormValues => ({
  ...DEFAULT_VALUES,
  ...(initialValues ?? {}),
  appIds:
    initialValues?.appIds && initialValues.appIds.length > 0
      ? initialValues.appIds
      : ['']
});

export const AppsFlyerS2SDrawer: React.FC<AppsFlyerS2SDrawerProps> = ({
  isOpen,
  onClose,
  initialValues,
  onSave,
  onDisable,
  isSaving = false
}) => {
  const [isDisableConfirmOpen, setIsDisableConfirmOpen] = useState(false);
  const [switchSyncKey, setSwitchSyncKey] = useState(0);
  const [showToken, setShowToken] = useState(false);
  const [serverGroupErrors, setServerGroupErrors] = useState<ServerGroupErrors>(
    {}
  );

  const form = useForm<AppsFlyerS2SFormValues>({
    resolver: zodResolver(appsFlyerS2SSchema),
    mode: 'onTouched',
    defaultValues: buildDefaultValues(initialValues)
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, touchedFields, isSubmitted }
  } = form;

  const isEnabled = useWatch({ control, name: 'enabled' });

  const tokenError =
    touchedFields.s2sToken || isSubmitted
      ? errors.s2sToken?.message
      : undefined;
  const appIdsTouched = isSubmitted || Boolean(touchedFields.appIds);
  const purchaseEventError =
    touchedFields.events?.['payment-success']?.eventName || isSubmitted
      ? errors.events?.['payment-success']?.eventName?.message
      : undefined;
  const refundEventError =
    touchedFields.events?.refund?.eventName || isSubmitted
      ? errors.events?.refund?.eventName?.message
      : undefined;

  useEffect(() => {
    if (isOpen) {
      reset(buildDefaultValues(initialValues));
      setServerGroupErrors({});
    }
  }, [isOpen, initialValues, reset]);

  const applyServerErrors = useCallback(
    (serverErrors: ServerFieldError[]) => {
      const groups: ServerGroupErrors = {};
      serverErrors.forEach(({ field, message }) => {
        if (field === 'appIds') {
          groups.appIds = message;
        } else if (field === 'events') {
          groups.events = message;
        } else {
          setError(
            normalizeServerField(field) as FieldPath<AppsFlyerS2SFormValues>,
            { type: 'server', message }
          );
        }
      });
      setServerGroupErrors(groups);
    },
    [setError]
  );

  const onSubmit = useCallback(
    async (values: AppsFlyerS2SFormValues) => {
      setServerGroupErrors({});
      try {
        await onSave?.(values);
      } catch (serverErrors) {
        if (Array.isArray(serverErrors)) {
          applyServerErrors(serverErrors as ServerFieldError[]);
        }
      }
    },
    [onSave, applyServerErrors]
  );

  const confirmDisable = useCallback(() => {
    setIsDisableConfirmOpen(false);
    setSwitchSyncKey((prev) => prev + 1);
    onDisable?.();
  }, [onDisable]);

  const cancelDisable = useCallback(() => {
    setIsDisableConfirmOpen(false);
    setSwitchSyncKey((prev) => prev + 1);
  }, []);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          hideCloseButton
          className="flex flex-col gap-0 overflow-hidden bg-white p-0 text-gray-900 w-full !max-w-[516px]"
        >
          <div className="flex items-center justify-between border-b border-base-sidebarBorder px-8 py-5">
            <Typography
              tag="h5"
              className="font-semibold text-base-mutedForeground"
            >
              AppsFlyer S2S Integration
            </Typography>
            <div className="flex items-center gap-5">
              <Button
                type="button"
                className="bg-indigo-700 hover:bg-button-hover-create"
                disabled={isSaving || !isEnabled}
                onClick={handleSubmit(onSubmit)}
                data-testid="apps-flyer-s2s-save"
              >
                Save
              </Button>
              <X
                size={16}
                className="cursor-pointer"
                onClick={onClose}
                data-testid="apps-flyer-s2s-close"
              />
            </div>
          </div>

          <Form {...form}>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-1 flex-col overflow-y-auto"
            >
              <section
                className={`flex items-start justify-between gap-1.5 p-9 ${SECTION_DIVIDER}`}
              >
                <div className="flex flex-col gap-1">
                  <Typography tag="h4">AppsFlyer S2S Integration</Typography>
                  <Typography className="text-sm text-gray-500">
                    Integrate web store purchases to mobile acquisition
                    campaigns via AppsFlyer.
                  </Typography>
                </div>
                <FormField
                  control={control}
                  name="enabled"
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      key={`enable-${value ? 'on' : 'off'}-${switchSyncKey}`}
                      defaultChecked={value}
                      checked={value}
                      onCheckedChange={(next: boolean) => {
                        if (!next && value) {
                          setIsDisableConfirmOpen(true);
                          return;
                        }
                        onChange(next);
                      }}
                      size="md"
                      data-testid="apps-flyer-s2s-enable-toggle"
                    />
                  )}
                />
              </section>

              <section className={`flex flex-col p-9 ${SECTION_DIVIDER}`}>
                <FormField
                  control={control}
                  name="s2sToken"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <Input
                      label="AppsFlyer S2S Token"
                      className="[&>div:first-child]:items-center [&>div:first-child_label]:font-bold [&>div:first-child_label]:text-p-m [&>div:first-child]:mb-[30px] [&>div:first-child_label_i]:font-black"
                      name="s2sToken"
                      isRequired
                      disabled={!isEnabled}
                      type={showToken ? 'text' : 'password'}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="Enter token"
                      state={tokenError ? 'error' : 'default'}
                      inputClassName={
                        tokenError ? 'border-base-destructive' : undefined
                      }
                      description={tokenError}
                      dataTestId="apps-flyer-s2s-token"
                      infoTooltipContent="See how to get the AppsFlyer S2S token in the docs"
                      infoTooltipSide="top"
                      rightIcon={
                        <FontAwesomeIcon
                          icon={
                            showToken
                              ? 'fa-regular fa-eye-slash'
                              : 'fa-regular fa-eye'
                          }
                          onClick={() => setShowToken((prev) => !prev)}
                        />
                      }
                      rightIconStyles="cursor-pointer text-base-mutedForeground hover:text-base-foreground"
                    />
                  )}
                />
              </section>

              <section className={`flex flex-col gap-3 p-9 ${SECTION_DIVIDER}`}>
                <FormField
                  control={control}
                  name="appIds"
                  render={({ field: { value, onChange, onBlur } }) => (
                    <div className="flex flex-col gap-2">
                      {value.map((id, index) => (
                        <ApplicationIdRow
                          key={index}
                          index={index}
                          value={id}
                          disabled={!isEnabled}
                          error={
                            appIdsTouched
                              ? errors.appIds?.[index]?.message
                              : undefined
                          }
                          onChange={(next) => {
                            const updated = [...value];
                            updated[index] = next;
                            onChange(updated);
                          }}
                          onBlur={onBlur}
                          onRemove={
                            value.length > 1
                              ? () =>
                                  onChange(value.filter((_, i) => i !== index))
                              : undefined
                          }
                          onCopy={() => navigator.clipboard.writeText(id ?? '')}
                        />
                      ))}

                      <Button
                        type="button"
                        variant="outline"
                        disabled={!isEnabled}
                        onClick={() => onChange([...value, ''])}
                        className="w-fit gap-2 border-dashed border-base-sidebarBorder text-base-foreground"
                        data-testid="apps-flyer-s2s-add-app-id"
                      >
                        <FontAwesomeIcon icon="fa-regular fa-plus" />
                        Application ID
                      </Button>
                    </div>
                  )}
                />
                {serverGroupErrors.appIds && (
                  <Typography className="text-xs text-base-destructive">
                    {serverGroupErrors.appIds}
                  </Typography>
                )}
              </section>

              <section className="flex flex-col px-9 pt-9 pb-10">
                <Typography tag="h5" className="font-bold mb-[30px]">
                  Event Name Configuration
                </Typography>

                <EventRow
                  control={control}
                  label="Purchase Event"
                  nameField="events.payment-success.eventName"
                  enabledField="events.payment-success.enabled"
                  error={purchaseEventError}
                  disabled={!isEnabled}
                  switchSyncKey={switchSyncKey}
                  testIdPrefix="apps-flyer-s2s-purchase"
                />

                <EventRow
                  control={control}
                  label="Refund Event"
                  nameField="events.refund.eventName"
                  enabledField="events.refund.enabled"
                  error={refundEventError}
                  disabled={!isEnabled}
                  switchSyncKey={switchSyncKey}
                  testIdPrefix="apps-flyer-s2s-refund"
                />
                {serverGroupErrors.events && (
                  <Typography className="text-xs text-base-destructive">
                    {serverGroupErrors.events}
                  </Typography>
                )}
              </section>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <DisableConfirmDialog
        isOpen={isDisableConfirmOpen}
        onConfirm={confirmDisable}
        onCancel={cancelDisable}
      />
    </>
  );
};
